// ============================================================
// entryTypes.ts
// THE ACTIVITY TIMELINE ENTRY-TYPE REGISTRY — one row per kind of thing
// that can appear on a client's Activity timeline. It WIDENS
// ACTION_TYPES (actionTypes.constants.ts — still the single source for the
// billable action vocabulary) to "any timeline entry": documents today, and
// invoices / payments / generated documents / case updates / SMS / fax
// tomorrow.
//
// Every timeline row renders from ONE shared anatomy (ClientActivityFeed):
//   [type tile] · type pill · case chip · title · who/when · detail line ·
//   billing slot (only when `bills`) · actions
// A type declares ONLY:
//   icon    — key into the feed's icon set
//   tone    — a palette token (palette.constants.ts). Firm/user Timeline
//             Colours overrides still win per type (resolveEntryTone).
//   family  — which filter segment it belongs to
//   bills   — carries time + units + £ (+ WIP/Billed status)
//   live    — false = not built yet: the type still shows as a greyed
//             "Soon" placeholder (filter segment, action menu, the
//             "coming to Activity" strip) so the roadmap stays visible.
//   soonAction — the placeholder action label for a future type
// Adding a type = one row here. Filters, counts, colours, totals and the
// billing slot all read this table.
// ============================================================
import { ACTION_TYPES } from './actionTypes.constants'
import { SHIPPED_DEFAULT_SCHEME } from './palette.constants'

export type EntryFamily = 'time' | 'corr' | 'docs' | 'bill' | 'sys'
export type EntryIcon =
  | 'call' | 'callIn' | 'letter' | 'memo' | 'mail' | 'upload'
  | 'gen' | 'invoice' | 'pay' | 'flag' | 'sms' | 'fax'
// Palette tokens (palette.constants.ts PALETTE keys).
export type ToneToken =
  | 'green' | 'blue' | 'sky' | 'teal' | 'emerald' | 'cyan'
  | 'indigo' | 'violet' | 'rose' | 'amber' | 'orange' | 'slate'

export interface EntryTypeDef {
  key: string
  label: string
  icon: EntryIcon
  tone: ToneToken
  family: EntryFamily
  bills: boolean
  live: boolean
  soonAction?: string
}

// Filter segments, in display order. `live: false` = greyed "Soon".
export const ENTRY_FAMILIES: { key: 'all' | EntryFamily; label: string; live: boolean }[] = [
  { key: 'all', label: 'All', live: true },
  { key: 'time', label: 'Time', live: true },
  { key: 'corr', label: 'Emails', live: true },
  { key: 'docs', label: 'Documents', live: true },
  // Issued bills, payments received + credit notes (GET /api/bill-events).
  { key: 'bill', label: 'Invoices', live: true },
  { key: 'sys', label: 'Case updates', live: false },
]

// Presentation for the LIVE billable action types, keyed by ACTION_TYPES
// value. An action type added to ACTION_TYPES without a row here still
// renders (memo icon, Time family, billable) — see actionEntry().
const ACTION_PRESENTATION: Record<string, { icon: EntryIcon; tone: ToneToken; family: EntryFamily }> = {
  call: { icon: 'call', tone: 'blue', family: 'time' },
  take_call: { icon: 'callIn', tone: 'sky', family: 'time' },
  letter: { icon: 'letter', tone: 'green', family: 'time' },
  memo: { icon: 'memo', tone: 'green', family: 'time' },
  email: { icon: 'mail', tone: 'violet', family: 'corr' },
}
const actionEntry = (value: string, label: string): EntryTypeDef => {
  const p = ACTION_PRESENTATION[value] ?? { icon: 'memo' as const, tone: 'green' as const, family: 'time' as const }
  return { key: value, label, icon: p.icon, tone: p.tone, family: p.family, bills: true, live: true }
}

export const ENTRY_TYPES: Record<string, EntryTypeDef> = {
  // Live — every billable action type (Activity rows).
  ...Object.fromEntries(ACTION_TYPES.map((t) => [t.value, actionEntry(t.value, t.label)])),
  // Live — client/case documents (FileAttachment), merged into the timeline.
  document: { key: 'document', label: 'Document', icon: 'upload', tone: 'teal', family: 'docs', bills: false, live: true },
  // Live — billing on the timeline (GET /api/bill-events — a read-time merge
  // of the bill tables, not Activity rows): an ISSUED bill, a payment
  // received, an issued credit note. `bills: false` — they're not billable
  // time; the feed shows their own £ (masked server-side for a viewer who
  // sees only their own £). Clicking one opens that bill (utils/openBill).
  // `invoice` is also the Log-time menu's "Invoice" action (raises a bill).
  invoice: { key: 'invoice', label: 'Invoice', icon: 'invoice', tone: 'amber', family: 'bill', bills: false, live: true },
  payment: { key: 'payment', label: 'Payment', icon: 'pay', tone: 'emerald', family: 'bill', bills: false, live: true },
  credit_note: { key: 'credit_note', label: 'Credit note', icon: 'invoice', tone: 'rose', family: 'bill', bills: false, live: true },
  // ── Future — NOT BUILT. Visible placeholders only. ──
  generated: { key: 'generated', label: 'Generated doc', icon: 'gen', tone: 'teal', family: 'docs', bills: false, live: false, soonAction: 'Generate document' },
  stage: { key: 'stage', label: 'Case update', icon: 'flag', tone: 'slate', family: 'sys', bills: false, live: false },
  sms: { key: 'sms', label: 'SMS', icon: 'sms', tone: 'indigo', family: 'corr', bills: true, live: false, soonAction: 'Send SMS' },
  fax: { key: 'fax', label: 'Fax', icon: 'fax', tone: 'slate', family: 'corr', bills: true, live: false, soonAction: 'Send fax' },
}

const FALLBACK: EntryTypeDef = { key: '__entry', label: 'Entry', icon: 'memo', tone: 'slate', family: 'time', bills: true, live: true }
export const entryTypeFor = (key: string | null | undefined): EntryTypeDef =>
  (key && ENTRY_TYPES[key]) || FALLBACK

export const FUTURE_ENTRY_TYPES: EntryTypeDef[] = Object.values(ENTRY_TYPES).filter((t) => !t.live)

// ── Tones ─────────────────────────────────────────────────────
// Tile / dot classes per palette token (light + dark). Literal full class
// strings so Tailwind's scanner keeps them. `green` uses the brand tokens
// (they re-theme on their own).
export const TONES: Record<ToneToken, string> = {
  green: 'bg-casevo-mint text-casevo-brand',
  blue: 'bg-[#e7efff] text-[#2563eb] dark:bg-[rgba(96,140,255,0.16)] dark:text-[#93b4ff]',
  sky: 'bg-[#e0f2fe] text-[#0284c7] dark:bg-[rgba(56,189,248,0.14)] dark:text-[#7dd3fc]',
  teal: 'bg-[#dcf5f1] text-[#0f766e] dark:bg-[rgba(45,212,191,0.13)] dark:text-[#5eead4]',
  emerald: 'bg-[#dcfce7] text-[#15803d] dark:bg-[rgba(74,222,128,0.13)] dark:text-[#86efac]',
  cyan: 'bg-[#cffafe] text-[#0e7490] dark:bg-[rgba(34,211,238,0.13)] dark:text-[#67e8f9]',
  indigo: 'bg-[#e0e7ff] text-[#4338ca] dark:bg-[rgba(129,140,248,0.16)] dark:text-[#a5b4fc]',
  violet: 'bg-[#efe7ff] text-[#7c3aed] dark:bg-[rgba(167,139,250,0.16)] dark:text-[#c4a7ff]',
  rose: 'bg-[#ffe4e6] text-[#be123c] dark:bg-[rgba(251,113,133,0.14)] dark:text-[#fda4af]',
  amber: 'bg-[#fdf0dc] text-[#b45309] dark:bg-[rgba(251,191,36,0.13)] dark:text-[#fbbf6a]',
  orange: 'bg-[#ffedd5] text-[#c2410c] dark:bg-[rgba(251,146,60,0.14)] dark:text-[#fdba74]',
  slate: 'bg-[#eef1f5] text-[#475569] dark:bg-[rgba(148,163,184,0.14)] dark:text-[#b6c2cf]',
}
const isTone = (t: string | undefined): t is ToneToken => !!t && t in TONES

// user override → firm override → shipped scheme → registry default.
// Same precedence as resolveActionColour (palette.constants.ts), so the
// admin/user Timeline Colours choices keep applying.
export const resolveEntryTone = (
  key: string,
  firmScheme: Record<string, string>,
  userScheme: Record<string, string>,
): ToneToken => {
  const t = userScheme[key] ?? firmScheme[key] ?? SHIPPED_DEFAULT_SCHEME[key]
  return isTone(t) ? t : entryTypeFor(key).tone
}
